import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { CreditCardComponent } from 'components';

/**Payment logos */
import Amex from '../../../../_img/registrationIcons/amex.png';
import Discover from '../../../../_img/registrationIcons/discover.png';
import Mastercard from '../../../../_img/registrationIcons/mastercard.png';
import Visa from '../../../../_img/registrationIcons/visa.png';

import StripeLock from '../../../../_img/registrationIcons/stripe-lock.webp';

import { LockOutlined, InfoOutlined } from '@mui/icons-material';
import { showErrorNotification } from 'common/helpers';
import { showSuccessNotification } from 'common/helpers';
import { AddCreditCardForAnAgentAPI } from 'services/agent';
import { AddAgentCCInfo } from 'services/agent';
import { CircularProgress } from '@mui/material';
import compose from 'recompose/compose';
import { withSnackbar } from 'notistack';
import CommonConfirmationDialog from 'components/CommonConfirmationDialog';
import { systemLogRequest } from 'common/helpers';

const styles = theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },

    stripeBody: {
        marginTop: -theme.spacing(2),
        marginBottom: theme.spacing(3),
        width: '100%',
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    paymentIcon: {
        width: '4em',
    },
    paymentIconHolder: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    centerDivider: {
        marginTop: theme.spacing(0),
        marginBottom: theme.spacing(1)
    },
    poweredByStripe: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: -theme.spacing(4),
        marginBottom: -theme.spacing(4)
    },
    stripeLogoImageWrapper: {
        maxWidth: '120px',
        textAlign: 'center',
        marginTop: theme.spacing(2),
        height: 'auto',
        objectFit: 'cover'
    },
    poweredByStripeImage: {
        width: '100%'
    },
    paymentSecureBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'row-reverse',
        width: '100%',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    securePayment: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        width: '100%',
    },
    paymentFooter: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'row',
        width: '100%',
    },
    footerButtons: {
        position: 'relative',
        display: 'flex',
    },
    portletFooter: {
        display: 'inline-block',
        paddingLeft: theme.spacing(2),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        width: '100%'
    },
    footerButtonsCenter: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    footerButtonsLeftRight: {
        flex: 1,
    },
    progressWrapper: {
        paddingTop: '8px',
        paddingBottom: '8px',
        display: 'flex',
        justifyContent: 'center'
    },
    dialogActions: {
        padding: 0
    }
});

const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
    margin: 0,
    padding: theme.spacing(2),
    '& .MuiIconButton-root': {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
}));

const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
    margin: 0,
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(1)
}));

const AddNewCreditCardDialog = (props) => {
    const { onClose, listgenUserID, customerID, listgenAgentFirstName, listgenAgentLastName, listgenAgentEmailAddress, agentHaveCard, getAgentCreditCards } = props;
    const [creditCardData, setCreditCardData] = useState(null);


    const [isCreditCardInfoValid, setCreditCardInfoValid] = useState(null);
    /**new credit card data */
    const [newCard, setNewCardData] = useState({
        nameOnCard: null,
        cardNumber: null,
        expiryMonth: null,
        expiryYear: null,
        cvc: null,
        isDefault: (agentHaveCard ? false : true)//if agent have card then by default no need to send is default true
    });

    /**loading variables */
    const [isLoading, setIsLoading] = useState(false);

    /**Open confirmation dialog */
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

    const handleFieldChange = (key, value) => {
        const newState = newCard;
        /**set the state */
        newState[key] = value;
        setNewCardData(newState);
    };
    const handleCreditCardData = (creditCardData) => {
        setCreditCardData(creditCardData);
    };

    const handleCreditCardValidity = (isValid) => {
        setCreditCardInfoValid(isValid);
    };

    /**Use effect to handle is valid */
    useEffect(() => {
        /**its a virtualized field change. not gonna show any error */
        if (isCreditCardInfoValid) {
            let expiryArray = creditCardData.expiry.split('/');
            handleFieldChange('nameOnCard', creditCardData.name);
            handleFieldChange('cardNumber', creditCardData.number.replace(/\D+/g, ''));
            handleFieldChange('expiryMonth', expiryArray[0]);
            handleFieldChange('expiryYear', '20' + expiryArray[1]);
            handleFieldChange('cvc', creditCardData.cvc);
        } else {
            /**make null all */
            handleFieldChange('nameOnCard', '');
            handleFieldChange('cardNumber', '');
            handleFieldChange('expiryMonth', '');
            handleFieldChange('expiryYear', '');
            handleFieldChange('cvc', '');
        }
    }, [isCreditCardInfoValid]);

    const handleYesClick = () => {
        /**now call according to whether agent have card or not */
        let nextFn = agentHaveCard ? addCreditCardForAnAgent : addFirstCreditCardForAnAgent;
        nextFn(true);
    };

    const handleNoClick = () => {
        /**now call according to whether agent have card or not */
        let nextFn = agentHaveCard ? addCreditCardForAnAgent : addFirstCreditCardForAnAgent;
        nextFn(false);
    };
    
    const closeConfirmationDialog = () => {
        setOpenConfirmationDialog(false);
    };

    const openDefaultCardDialog = () => {
        setOpenConfirmationDialog(true);
    };

    const addCreditCardForAnAgent = (isDefault) => {
        /**make loading true */
        const { nameOnCard, cardNumber, expiryMonth, expiryYear, cvc } = newCard;
        // Card fields here are redacted by SENSITIVE_KEYS in exceptionTracker before
        // this reaches Sentry — kept so failures are debuggable (shape, isDefault, response).
        const requestBody = { nameOnCard, cardNumber, expiryMonth, expiryYear, cvc, isDefault };
        setIsLoading(true);
        try {
            AddCreditCardForAnAgentAPI(nameOnCard, listgenUserID, customerID, listgenAgentEmailAddress, cardNumber, expiryMonth, expiryYear, cvc, isDefault)
                .then(data => {
                    if (data) {
                        setIsLoading(false);
                        /**reload the cards now */
                        showSuccessNotification(props, "Card Added.");
                        onClose && onClose();
                        getAgentCreditCards();
                    } else {
                        //assign empty data
                        setIsLoading(false);
                        systemLogRequest('credit_card', 104, {}, { requestBody, responseBody: data });
                        showErrorNotification(props, "Unable to add your credit card on stripe. Error Code: 104");
                    }
                })
                .catch(err => {
                    systemLogRequest('credit_card', 105, err, { requestBody });
                    showErrorNotification(props, "Unable to process request. Error Code: 105");
                    setIsLoading(false);
                });
        } catch (err) {
            systemLogRequest('credit_card', 105, err, { requestBody });
            showErrorNotification(props, "Unable to process request. Error Code: 106");
            setIsLoading(false);
        }
    };

    const addFirstCreditCardForAnAgent = () => {
        /**make loading true */
        const { nameOnCard, cardNumber, expiryMonth, expiryYear, cvc, isDefault } = newCard;
        const requestBody = { nameOnCard, cardNumber, expiryMonth, expiryYear, cvc, isDefault };
        setIsLoading(true);
        try {
            AddAgentCCInfo(listgenUserID, listgenAgentEmailAddress, cardNumber, expiryMonth, expiryYear, cvc, nameOnCard)
                .then(data => {
                    if (data) {
                        setIsLoading(false);
                        /**reload the cards now */
                        showSuccessNotification(props, "Card Added.");
                        onClose && onClose();
                        getAgentCreditCards();
                    } else {
                        //assign empty data
                        setIsLoading(false);
                        systemLogRequest('credit_card', 101, {}, { requestBody, responseBody: data });
                        showErrorNotification(props, "Unable to process request. Error Code: 101");
                    }
                })
                .catch(err => {
                    systemLogRequest('credit_card', 102, err, { requestBody });
                    showErrorNotification(props, "Unable to process request. Error Code: 102");
                    setIsLoading(false);
                });
        } catch (err) {
            systemLogRequest('credit_card', 103, err, { requestBody });
            showErrorNotification(props, "Unable to process request. Error Code: 103");
            setIsLoading(false);
        }
    };

    return (
        <div>
            {openConfirmationDialog ?
                <CommonConfirmationDialog
                    title={"Do you want to make this card your default card?"}
                    subtitle={"Default card will be used to charge for the PMA. You can change default card preference later also."}
                    closeFn={closeConfirmationDialog}
                    methodToCallOnConfirmation={handleYesClick}
                    methodToCallOnCancel={handleNoClick}
                    confirmationID={null}
                    confirmationButtonText={"Yes"}
                    cancelButtonText={"No"}
                    stopAutoConfirmationFnExecution={true}
                /> : ''}
            <Dialog 
                disableEscapeKeyDown={true}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        onClose();
                    }
                }}
                aria-labelledby="customized-dialog-title" 
                open={true} 
                maxWidth="lg" 
                fullWidth={false}
                sx={{
                    '& .MuiDialog-paper': { overflowX: 'hidden', width: '1040px', maxWidth: '1040px' },
                    '& .MuiDialogContent-root': { overflowX: 'hidden' }
                }}
            >
            <DialogTitle id="customized-dialog-title" onClose={onClose}>
                <Typography variant="h6">
                    {agentHaveCard ? 'Add a new Credit Card' : 'Add First Credit Card'}
                </Typography>
                </DialogTitle>
                <DialogContent dividers={true}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: -32, marginBottom: -32 }}>
                        <div style={{ maxWidth: '120px', textAlign: 'center', marginTop: 16, height: 'auto', objectFit: 'cover' }}>
                            <img
                                src={StripeLock}
                                style={{ width: '100%' }}
                                alt="Powered By Stripe"
                            />
                        </div>
                    </div>
                    
                    <CreditCardComponent
                        holdername={newCard.nameOnCard}
                        handleCreditCardData={handleCreditCardData}
                        handleCreditCardValidity={handleCreditCardValidity}
                        cardNumber={newCard.cardNumber}
                        expiryMonth={newCard.expiryMonth}
                        expiryYear={newCard.expiryYear}
                        cvc={newCard.cvc}
                    />

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'row-reverse', width: '100%', marginTop: 16, marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <div>
                                <img
                                    src={Amex}
                                    alt="American Express"
                                    style={{ width: '4em' }}
                                />
                            </div>
                            <div>
                                <img
                                    src={Discover}
                                    alt="Discover"
                                    style={{ width: '4em' }}
                                />
                            </div>
                            <div>
                                <img
                                    src={Visa}
                                    alt="Visa"
                                    style={{ width: '4em' }}
                                />
                            </div>
                            <div>
                                <img
                                    src={Mastercard}
                                    alt="Master Card"
                                    style={{ width: '4em' }}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                            <IconButton>
                                <LockOutlined />
                            </IconButton>
                            <Typography variant="subtitle2">
                                Secure Stripe Payment (256 bit SSL)
                            </Typography>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row', width: '100%' }}>
                        <div>
                            <IconButton color="primary">
                                <InfoOutlined fontSize='large' />
                            </IconButton>
                        </div>
                        <div>
                            <Typography variant="subtitle2">
                                Your credit card information is encrypted, saved and held on file by STRIPE.
                            </Typography>
                            <Typography variant="subtitle2">
                                Your credit card will be automatically charged with each mailing.
                            </Typography>
                        </div>
                    </div>
                </DialogContent>
                {isLoading ?
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
                        <CircularProgress />
                    </div>
                    :
                    <DialogActions sx={{ px: 2, py: 1.5, display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{ 
                                whiteSpace: 'nowrap', 
                                minWidth: '80px', 
                                justifySelf: 'start',
                                color: '#1a1a1a',
                                borderColor: '#d0d0d0',
                                backgroundColor: '#fff',
                                borderRadius: '5px',
                                padding: '5px 16px',
                                fontSize: '14px',
                                fontWeight: 500,
                                textTransform: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                    borderColor: '#bbb',
                                    backgroundColor: '#fafafa',
                                    boxShadow: 'none',
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={event => openDefaultCardDialog()}
                            color="primary"
                            disabled={!isCreditCardInfoValid}
                            size="small"
                            sx={{ whiteSpace: 'nowrap', minWidth: '96px', justifySelf: 'center' }}
                        >
                            Add Card
                        </Button>
                        <div style={{ width: 0 }}></div>
                    </DialogActions>
                }
            </Dialog>
        </div>
    );
}

export default compose(
    withSnackbar
)(AddNewCreditCardDialog);
